/**
 * TaskTimeline — horizontal milestone-style timeline.
 * Tasks are plotted on a continuous horizontal line by due_date.
 * Each task is a bubble (size by priority) with the date label above and
 * the title below. Click a bubble to open the task. Tasks without due_date
 * are bucketed in an "Undated" lane to the right.
 */
import { useMemo } from "react";
import { Sparkles, Calendar as CalendarIcon } from "lucide-react";

const PRIO_COLOR = {
  high: "bg-red-600 border-red-700",
  medium: "bg-zinc-700 border-zinc-800",
  low: "bg-emerald-600 border-emerald-700",
};
const PRIO_RING = {
  high: "ring-red-200",
  medium: "ring-zinc-300",
  low: "ring-emerald-200",
};
const PRIO_SIZE = { high: "w-5 h-5", medium: "w-4 h-4", low: "w-3 h-3" };

function fmtDay(iso) {
  if (!iso) return "—";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00Z" : ""));
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function TaskTimeline({ tasks, onOpenTask }) {
  const { datedSorted, undated, minTime, maxTime } = useMemo(() => {
    const dated = tasks.filter((t) => t.due_date);
    const undated = tasks.filter((t) => !t.due_date);
    const sorted = [...dated].sort((a, b) => (a.due_date < b.due_date ? -1 : 1));
    let minT = Infinity, maxT = -Infinity;
    for (const t of sorted) {
      const ts = Date.parse(t.due_date + (t.due_date.length === 10 ? "T00:00:00Z" : ""));
      if (!isNaN(ts)) { minT = Math.min(minT, ts); maxT = Math.max(maxT, ts); }
    }
    if (minT === Infinity || minT === maxT) {
      // Single point or empty — pad ±1 day so it doesn't collapse
      const center = isFinite(minT) ? minT : Date.now();
      minT = center - 86400000;
      maxT = center + 86400000;
    }
    return { datedSorted: sorted, undated, minTime: minT, maxTime: maxT };
  }, [tasks]);

  const span = maxTime - minTime;
  const positionPct = (iso) => {
    const ts = Date.parse(iso + (iso.length === 10 ? "T00:00:00Z" : ""));
    return Math.max(2, Math.min(98, ((ts - minTime) / span) * 96 + 2));
  };

  if (tasks.length === 0) {
    return (
      <div className="border border-dashed border-zinc-300 px-6 py-12 text-center" data-testid="timeline-empty">
        <CalendarIcon className="w-6 h-6 text-zinc-400 mx-auto mb-3" strokeWidth={1.5} />
        <p className="text-sm text-zinc-500">No tasks to plot yet.</p>
      </div>
    );
  }

  return (
    <div className="border border-zinc-200 bg-white" data-testid="task-timeline">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-600">
          Timeline · {tasks.length} task{tasks.length === 1 ? "" : "s"}
        </p>
        <p className="text-[10px] text-zinc-400 hidden sm:block">
          <Sparkles className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
          Click a bubble to open
        </p>
      </div>

      {datedSorted.length > 0 ? (
        <div className="relative overflow-x-auto" data-testid="timeline-track-wrapper">
          {/* Inner has min-width so dense timelines scroll horizontally on mobile */}
          <div className="relative min-w-[720px] px-4 pt-8 pb-12">
            {/* The line */}
            <div className="absolute left-4 right-4 top-1/2 h-px bg-zinc-300" />

            {/* Endpoints */}
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-2 h-2 bg-zinc-300 rounded-full" />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 bg-zinc-300 rounded-full" />

            {/* Bubbles */}
            {datedSorted.map((t, i) => {
              const left = positionPct(t.due_date);
              const above = i % 2 === 0; // alternate above/below to reduce label collisions
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onOpenTask?.(t)}
                  className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 group"
                  style={{ left: `${left}%` }}
                  data-testid={`timeline-task-${t.id}`}
                  title={t.title}
                >
                  {/* Date label */}
                  <span
                    className={`absolute left-1/2 -translate-x-1/2 text-[10px] font-mono text-zinc-500 whitespace-nowrap ${above ? "-top-7" : "top-4"}`}
                  >
                    {fmtDay(t.due_date)}
                  </span>
                  {/* Bubble */}
                  <span
                    className={`block ${PRIO_SIZE[t.priority] || PRIO_SIZE.medium} rounded-full ${PRIO_COLOR[t.priority] || PRIO_COLOR.medium} border-2 ring-4 ${PRIO_RING[t.priority] || PRIO_RING.medium} ring-white group-hover:scale-125 transition-transform ${t.completed ? "opacity-40" : ""}`}
                  />
                  {/* Title label */}
                  <span
                    className={`absolute left-1/2 -translate-x-1/2 text-[11px] text-zinc-800 max-w-[140px] truncate ${above ? "top-7 text-center" : "-top-7 text-center"} ${t.completed ? "line-through text-zinc-400" : ""}`}
                  >
                    {t.title}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="px-4 py-6 text-xs text-zinc-500 italic">No tasks have due dates yet. Add a due date to see them on the timeline.</p>
      )}

      {undated.length > 0 && (
        <div className="border-t border-zinc-200 px-4 py-3" data-testid="timeline-undated">
          <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 mb-2">
            No due date · {undated.length}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {undated.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => onOpenTask?.(t)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 border border-zinc-300 text-xs hover:border-zinc-900 hover:bg-zinc-50 transition-colors ${t.completed ? "line-through text-zinc-400" : "text-zinc-700"}`}
                data-testid={`timeline-undated-${t.id}`}
              >
                <span className={`w-2 h-2 rounded-full ${PRIO_COLOR[t.priority] || PRIO_COLOR.medium} border`} />
                {t.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
